import { spaceOf } from '../../utils/useCases';

/**
 * The booking window's rules, for the editor.
 *
 * ── ONE RULE, FOUR DEPLOYABLES (ADR-0014) ─────────────────────────────────
 *
 * The canonical copy is `new-backend/admin/src/lib/bookingWindow.ts`, which
 * owns validation and storage. The admin-ui cannot import it (separate
 * deploys), which is the same arrangement `serviceConfigurationRules.ts` has
 * with `serviceConfiguration.ts`. The four copies name each other and each is
 * pinned by its own tests:
 *
 *   - `new-backend/admin/src/lib/bookingWindow.ts` — canonical; write-side.
 *   - `backend/src/core/lib/bookingWindow.ts` — enforcement + availability.
 *   - THIS FILE — the editor. Pinned by `bookingWindowRules.test.ts`.
 *   - `apps/end-user/.../hooks/useBookingWindow.ts` — the customer's view.
 *
 * Change a rule and change all four in the same PR. A default that drifts here
 * makes the form show a tenant a number the API does not apply.
 *
 * ── The unit split, which is the whole reason this file has conversions ───
 *
 * Everything is STORED in minutes, so the guard, the availability gate and the
 * published number read one number and cannot disagree at the boundary. What a
 * tenant CHOOSES depends on how the use case books:
 *
 *   - a FULL-DAY use case (apartment, venue) books whole days, so a buffer of
 *     anything between one minute and one day has exactly one effect — it
 *     blocks the checkout day. The tenant therefore picks DAYS, and the write
 *     endpoint refuses a stored value that is not a whole day.
 *   - every other use case books a time of day, so the tenant picks HOURS.
 */

/** Minutes in a day. */
export const MINUTES_PER_DAY = 1440;

/** Minutes in an hour. */
export const MINUTES_PER_HOUR = 60;

/** Use cases with a whole-day booking window (`space.windowInDays`,
 *  `utils/useCases.ts`) rather than one in times of day. */
export const isFullDayUseCase = (useCase: string): boolean =>
    spaceOf(useCase)?.windowInDays === true;

/** The unit a tenant chooses a buffer in, for this use case. */
export type BufferUnit = 'days' | 'hours';

export const bufferUnitFor = (useCase: string): BufferUnit =>
    isFullDayUseCase(useCase) ? 'days' : 'hours';

/** Minutes per one step of this use case's chosen unit. */
export const minutesPerUnit = (useCase: string): number =>
    isFullDayUseCase(useCase) ? MINUTES_PER_DAY : MINUTES_PER_HOUR;

export interface BookingWindowConfig {
  leadMinutes: number;
  horizonMonths: number;
  followUpMinutes: number;
  /** Shortest stay a customer may book, in nights — apartments only. */
  minNights: number;
}

/** Use cases a minimum stay applies to (`space.minStay`) — the backend copies
 *  call it `MIN_STAY_USE_CASES`. */
export const hasMinStay = (useCase: string): boolean =>
    spaceOf(useCase)?.minStay === true;

/**
 * ── The room-family numbers changed on 2026-09-23 (PO) ───────────────────
 *
 * Five days was a transcription of what the room screens hardcoded, never a
 * decision; two days is the standard. Apartments also gain a one-day
 * follow-up — they used to be spaced by a hardcoded five-day rule in the old
 * backend that this config could not reach.
 *
 * Venue keeps zero follow-up on purpose, not by omission: a meeting room is
 * not cleaned for a day between bookings.
 *
 * Copies, kept together: `backend/src/core/lib/bookingWindow.ts` and
 * `new-backend/admin/src/lib/bookingWindow.ts`, pinned by
 * `backend/tests/bookingWindow.test.ts`.
 */
/** The whole-day block; its follow-up is the use case's own
 *  `space.followUpDefaultDays` (apartment 1, venue 0). */
const FULL_DAY_DEFAULT: Omit<BookingWindowConfig, 'followUpMinutes'> = {
    leadMinutes: 2 * MINUTES_PER_DAY,
    horizonMonths: 18,
    minNights: 1,
};

const SUB_DAY_DEFAULT: BookingWindowConfig = {
    leadMinutes: 0,
    horizonMonths: 1,
    followUpMinutes: 0,
    minNights: 1,
};

/**
 * The documented default for a use case nobody has configured — the numbers the
 * end-user screens hardcoded before this setting existed. Shown in the form as
 * the starting value, so opening the section and saving it changes nothing.
 */
export const defaultBookingWindow = (useCase: string): BookingWindowConfig => {
    const space = spaceOf(useCase);
    if (space?.windowInDays) {
        return { ...FULL_DAY_DEFAULT, followUpMinutes: space.followUpDefaultDays * MINUTES_PER_DAY };
    }
    return { ...SUB_DAY_DEFAULT };
};

/** Bounds, mirroring the canonical copy (`new-backend/admin/src/lib/bookingWindow.ts`,
 *  which enforces the rest and answers with the field it refused). */
export const MIN_HORIZON_MONTHS = 1;
export const MAX_MIN_NIGHTS = 30;

/** Stored minutes → the number shown in the field. */
export const toUnit = (minutes: number, useCase: string): number =>
    minutes / minutesPerUnit(useCase);

/** The number in the field → stored minutes. */
export const toMinutes = (value: number, useCase: string): number =>
    value * minutesPerUnit(useCase);

/**
 * Resolve a stored window against the defaults, field by field — the same
 * tolerance the end-user app and the API apply, so the form shows exactly what
 * a customer will be offered rather than a second interpretation of the JSONB.
 */
export const resolveBookingWindow = (
    stored: Partial<BookingWindowConfig> | undefined | null,
    useCase: string,
): BookingWindowConfig => {
    const fallback = defaultBookingWindow(useCase);
    const usable = (value: unknown, min: number): number | null =>
        typeof value === 'number' && Number.isInteger(value) && value >= min ? value : null;

    return {
        leadMinutes: usable(stored?.leadMinutes, 0) ?? fallback.leadMinutes,
        horizonMonths: usable(stored?.horizonMonths, MIN_HORIZON_MONTHS) ?? fallback.horizonMonths,
        followUpMinutes: usable(stored?.followUpMinutes, 0) ?? fallback.followUpMinutes,
        minNights: hasMinStay(useCase)
            ? usable(stored?.minNights, 1) ?? fallback.minNights
            : fallback.minNights,
    };
};

